import { numberToPrettyString } from '@cian/utils';
import { pathOr } from 'ramda';

import { IOfferForMobileSiteSchema } from '../repositories/search-engine/entities/offer/OfferForMobileSiteSchema';
import { IOfferFeaturesSchema } from '../repositories/search-engine/entities/offer_features/OfferFeaturesSchema';
import { ISearchOffersMobileSiteResponse } from '../repositories/search-engine/v2/search-offers-mobile-site';
import { ECurrency, EPeriod, IPrice, TOffers } from '../types/offers';

export const NBSP = '\u00a0';

export function prepareOffers(offersResponse: ISearchOffersMobileSiteResponse): TOffers {
  return offersResponse.offers.map(offer => {
    return {
      id: offer.id,
      url: prepareOfferUrl(offer),
      price: preparePrice(offer),
      photoUrl: preparePhoto(offer),
      features: prepareFeatures(offer.features),
      underground: prepareUnderground(offer),
    };
  });
}

function prepareUnderground(offer: IOfferForMobileSiteSchema): string | null {
  try {
    const name = offer.geo.undergrounds[0].name;
    if (!name) {
      return null;
    }

    return `м. ${name}`;
  } catch (_) {
    return null;
  }
}

function prepareFeatures({
  objectType,
  totalArea,
  landStatus,
  landArea,
  floorInfo,
  bedroomsCount,
}: IOfferFeaturesSchema): string[] {
  return [objectType, totalArea, landArea, landStatus, floorInfo, bedroomsCount].filter(Boolean) as string[];
}

function preparePhoto(offer: IOfferForMobileSiteSchema): string {
  try {
    return offer.media[0].previewUrl || '';
  } catch (_) {
    return '';
  }
}

function prepareOfferUrl(offer: IOfferForMobileSiteSchema): string {
  const isCianPartner = pathOr(false, ['isCianPartner'], offer);
  const externalOfferUrl = pathOr(null, ['externalOfferUrl'], offer);

  if (isCianPartner && externalOfferUrl) {
    return externalOfferUrl;
  }

  return `/${offer.dealType}/${offer.offerType}/${offer.id}`;
}

function preparePrice(offer: IOfferForMobileSiteSchema): string {
  const isOfferDailyCategory = offer.category.includes('daily');

  const formattedPrice = priceToString(offer.price);

  return isOfferDailyCategory ? `от${NBSP}${formattedPrice}` : formattedPrice;
}

function priceToString(price: IPrice): string {
  const { value, currency, period } = price;
  const formattedValue = numberToPrettyString(value);
  const currencySymbol = currencyToString(currency);
  const periodString = period ? `/${periodToString(period)}` : null;

  return `${formattedValue} ${currencySymbol}${periodString || ''}`;
}

function currencyToString(currency: ECurrency) {
  switch (currency) {
    case ECurrency.Eur:
      return '€';
    case ECurrency.Usd:
      return '$';
    case ECurrency.Rur:
    default:
      return '₽';
  }
}

function periodToString(period?: EPeriod | null): string | null {
  switch (period) {
    case EPeriod.Day:
      return 'сутки';
    case EPeriod.Month:
      return 'мес.';
  }

  return null;
}
